import React, { useEffect, useState, useCallback } from 'react';
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import {
    Card, CardContent, CardHeader, CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Users, Wallet, Clock, TrendingUp,
    Search, ChevronLeft, ChevronRight, RefreshCw, Filter,
} from 'lucide-react';
import { agentsApi, Agent } from "../../api/agents.api";
import { transactionsApi, Transaction, TransactionType, TransactionStatus } from "../../api/transactions.api";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";

// ─── Helpers ────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
    depot_bs: 'Dépôt BS',
    depo_bs: 'Dépôt BS',
    retrait_bs: 'Retrait BS',
    depot_sol: 'Dépôt SOL',
    depo_sol: 'Dépôt SOL',
    transfer: 'Transfert',
    transfert: 'Transfert',
    commission_solide: 'Commission',
};

const TYPE_COLORS: Record<string, string> = {
    depot_bs: 'bg-green-100 text-green-800',
    depo_bs: 'bg-green-100 text-green-800',
    depot_sol: 'bg-emerald-100 text-emerald-800',
    depo_sol: 'bg-emerald-100 text-emerald-800',
    retrait_bs: 'bg-red-100 text-red-800',
    transfer: 'bg-blue-100 text-blue-800',
    transfert: 'bg-blue-100 text-blue-800',
    commission_solide: 'bg-purple-100 text-purple-800',
};

const STATUS_COLORS: Record<string, string> = {
    reussi: 'bg-green-100 text-green-800',
    en_cours: 'bg-yellow-100 text-yellow-800',
    echoue: 'bg-red-100 text-red-800',
    annule: 'bg-gray-100 text-gray-800',
    completed: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS: Record<string, string> = {
    reussi: 'Réussi',
    en_cours: 'En cours',
    echoue: 'Échoué',
    annule: 'Annulé',
    completed: 'Réussi',
    pending: 'En cours',
    failed: 'Échoué',
    cancelled: 'Annulé',
};

function fmtMoney(v: string | number, devise = 'HTG') {
    return new Intl.NumberFormat('fr-HT', { minimumFractionDigits: 2 }).format(Number(v)) + ' ' + devise;
}

function fmtDate(s: string) {
    return new Date(s).toLocaleString('fr-HT', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function AgentMonitor() {
    const { toast } = useToast();

    // Agent selector
    const [agents, setAgents] = useState<Agent[]>([]);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
    const [agentsSearch, setAgentsSearch] = useState<string>('');

    // KPI
    const [pendingCount, setPendingCount] = useState(0);
    const [clientsCount, setClientsCount] = useState(0);

    // Transactions tab
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [txCount, setTxCount] = useState(0);
    const [txPage, setTxPage] = useState(1);
    const [txLoading, setTxLoading] = useState(false);
    const PAGE_SIZE = 15;

    // Filters
    const [filterType, setFilterType] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterDateDebut, setFilterDateDebut] = useState('');
    const [filterDateFin, setFilterDateFin] = useState('');
    const [filterSearch, setFilterSearch] = useState('');

    // Clients tab
    const [clients, setClients] = useState<any[]>([]);
    const [clientsPage, setClientsPage] = useState(1);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [clientSearch, setClientSearch] = useState('');

    // ── Load all agents on mount ──
    useEffect(() => {
        agentsApi.getAll().then(res => {
            setAgents(res.agents || []);
        }).catch(() => {
            toast({ type: 'error', title: 'Pa ka chaje lis ajan yo' });
        });
    }, []);

    // ── When agent selected: load detail + KPI ──
    useEffect(() => {
        if (!selectedAgentId) {
            setSelectedAgent(null);
            // Fetch global pending count
            transactionsApi.getWithdrawalRequests({ status: 'pending' })
                .then(res => setPendingCount((res.requests ?? []).length))
                .catch(() => setPendingCount(0));
            setClientsCount(0);
            setTxPage(1);
            setFilterType('all');
            setFilterStatus('all');
            setFilterDateDebut('');
            setFilterDateFin('');
            setFilterSearch('');
            return;
        }

        agentsApi.getById(selectedAgentId).then(res => {
            setSelectedAgent(res.agent);
        });

        transactionsApi.getWithdrawalRequests({ agent_id: selectedAgentId, status: 'pending' })
            .then(res => setPendingCount((res.requests ?? []).length))
            .catch(() => setPendingCount(0));

        agentsApi.getClients(selectedAgentId, { page: 1 })
            .then(res => setClientsCount(res.total ?? 0))
            .catch(() => setClientsCount(0));

        setTxPage(1);
        setFilterType('all');
        setFilterStatus('all');
        setFilterDateDebut('');
        setFilterDateFin('');
        setFilterSearch('');
    }, [selectedAgentId]);

    // ── Fetch transactions ──
    const fetchTransactions = useCallback(async (page = 1) => {
        setTxLoading(true);
        try {
            const res = await transactionsApi.list({
                created_by_id: selectedAgent?.userId || undefined,
                type: (filterType !== 'all' ? filterType as TransactionType : undefined),
                status: (filterStatus !== 'all' ? filterStatus as TransactionStatus : undefined),
                date_debut: filterDateDebut || undefined,
                date_fin: filterDateFin || undefined,
                search: filterSearch || undefined,
                page,
                page_size: PAGE_SIZE,
                group_sol: false,
            });
            setTransactions(res.results || []);
            setTxCount(res.count || 0);
            setTxPage(page);
        } catch {
            toast({ type: 'error', title: 'Erè chajman tranzaksyon yo' });
        } finally {
            setTxLoading(false);
        }
    }, [selectedAgent, filterType, filterStatus, filterDateDebut, filterDateFin, filterSearch]);

    useEffect(() => {
        fetchTransactions(1);
    }, [selectedAgent, filterType, filterStatus, filterDateDebut, filterDateFin]);

    // ── Fetch clients ──
    const fetchClients = useCallback(async (page = 1) => {
        if (!selectedAgentId) return;
        setClientsLoading(true);
        try {
            const res = await agentsApi.getClients(selectedAgentId, { page, search: clientSearch || undefined });
            setClients(res.comptes || []);
            setClientsPage(page);
        } catch {
            toast({ type: 'error', title: 'Erè chajman kliyan yo' });
        } finally {
            setClientsLoading(false);
        }
    }, [selectedAgentId, clientSearch]);

    // Auto-chaje kliyan lè ajan seleksyone
    useEffect(() => {
        if (selectedAgentId) fetchClients(1);
    }, [selectedAgentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const totalTxPages = Math.max(1, Math.ceil(txCount / PAGE_SIZE));

    const totalSoldeGlobal = agents.reduce((sum, a) => sum + (a.solde || 0), 0);
    const totalCommissionGlobal = agents.reduce((sum, a) => sum + (a.commission || 0), 0);
    const totalCommissionUsdGlobal = agents.reduce((sum, a) => sum + (a.commissionUsd || 0), 0);
    const activeAgentsCount = agents.filter(a => a.actif).length;

    const filteredAgents = agents.filter(a => {
        const search = agentsSearch.toLowerCase();
        const fullName = `${a.client?.prenom ?? ''} ${a.client?.nom ?? ''} ${a.name ?? ''}`.toLowerCase();
        const code = (a.code ?? '').toLowerCase();
        return fullName.includes(search) || code.includes(search);
    });

    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="flex h-screen overflow-hidden bg-background">
            <Sidebar />
            <div className="flex flex-col flex-1 overflow-hidden">
                <Topbar />
                <main className="flex-1 overflow-y-auto p-6 space-y-6">

                    {/* Breadcrumb */}
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbLink href="/agents">Agents</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbPage>Monitor Ajan</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    {/* Header + Agent Selector */}
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">Monitor Ajan</h1>
                            <p className="text-sm text-muted-foreground">Swivi konplè aktivite ak tranzaksyon ajan yo</p>
                        </div>
                        <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                            <SelectTrigger className="w-full sm:w-72">
                                <SelectValue placeholder="Seleksyone yon ajan…" />
                            </SelectTrigger>
                            <SelectContent>
                                {agents.map(a => (
                                    <SelectItem key={a.id} value={a.id}>
                                        {a.client?.prenom ?? ''} {a.client?.nom ?? a.name ?? a.id} — {a.code}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {!selectedAgent ? (
                        <>
                            {/* KPI Cards Global */}
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                <Card>
                                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                        <CardTitle className="text-sm font-medium">Balans Global</CardTitle>
                                        <Wallet className="h-4 w-4 text-blue-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{fmtMoney(totalSoldeGlobal)}</div>
                                        <p className="text-xs text-muted-foreground">Tout ajan yo konbine</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                        <CardTitle className="text-sm font-medium">Komisyon Global</CardTitle>
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{fmtMoney(totalCommissionUsdGlobal, 'USD')}</div>
                                        <p className="text-xs text-muted-foreground">{fmtMoney(totalCommissionGlobal)}</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                        <CardTitle className="text-sm font-medium">Ajan yo</CardTitle>
                                        <Users className="h-4 w-4 text-purple-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{agents.length}</div>
                                        <p className="text-xs text-muted-foreground">{activeAgentsCount} aktif • {agents.length - activeAgentsCount} inaktif</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                        <CardTitle className="text-sm font-medium">Demann Pending</CardTitle>
                                        <Clock className="h-4 w-4 text-orange-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className={cn("text-2xl font-bold", pendingCount > 0 && "text-orange-600")}>
                                            {pendingCount}
                                        </div>
                                        <p className="text-xs text-muted-foreground">demann retrait an atant global</p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Tabs: Lis Ajan yo | Tranzaksyon Global */}
                            <Tabs defaultValue="agents-list">
                                <TabsList>
                                    <TabsTrigger value="agents-list">Lis Ajan yo ({agents.length})</TabsTrigger>
                                    <TabsTrigger value="global-transactions">Tranzaksyon Global ({txCount})</TabsTrigger>
                                </TabsList>

                                <TabsContent value="agents-list" className="space-y-4">
                                    {/* Search agents */}
                                    <div className="flex gap-3">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                placeholder="Rechèch ajan pa non, prenon oswa kòd..."
                                                value={agentsSearch}
                                                onChange={e => setAgentsSearch(e.target.value)}
                                                className="pl-8"
                                            />
                                        </div>
                                    </div>

                                    {/* Agents Table */}
                                    <Card>
                                        <CardContent className="p-0">
                                            {filteredAgents.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                                                    <Users className="h-8 w-8 opacity-30" />
                                                    <p className="text-sm">Pa gen okenn ajan ki matche ak rechèch la</p>
                                                </div>
                                            ) : (
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>Kòd</TableHead>
                                                            <TableHead>Ajan</TableHead>
                                                            <TableHead>Kalite</TableHead>
                                                            <TableHead className="text-right">Balans</TableHead>
                                                            <TableHead className="text-right">Komisyon USD</TableHead>
                                                            <TableHead className="text-right">Komisyon HTG</TableHead>
                                                            <TableHead>Statut</TableHead>
                                                            <TableHead className="text-right">Aksyon</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {filteredAgents.map(a => (
                                                            <TableRow key={a.id} className="hover:bg-slate-50 cursor-pointer animate-fade-in" onClick={() => setSelectedAgentId(a.id)}>
                                                                <TableCell className="font-mono text-sm font-semibold text-blue-600">{a.code}</TableCell>
                                                                <TableCell>
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-xs">
                                                                            {(a.client?.prenom?.[0] ?? 'A')}{(a.client?.nom?.[0] ?? '')}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium text-sm block">
                                                                                {a.client?.prenom} {a.client?.nom ?? a.name}
                                                                            </span>
                                                                            <span className="text-xs text-muted-foreground block">{a.client?.telephone ?? '—'}</span>
                                                                        </div>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="text-sm">
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {a.type}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell className="text-right font-semibold text-sm">
                                                                    {fmtMoney(a.solde ?? 0)}
                                                                </TableCell>
                                                                <TableCell className="text-right font-semibold text-sm text-green-600">
                                                                    {fmtMoney(a.commissionUsd ?? 0, 'USD')}
                                                                </TableCell>
                                                                <TableCell className="text-right font-semibold text-sm text-muted-foreground">
                                                                    {fmtMoney(a.commission ?? 0)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Badge className={a.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                                                                        {a.actif ? 'Aktif' : 'Inaktif'}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                                                                    <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800 font-semibold" onClick={() => setSelectedAgentId(a.id)}>
                                                                        Monitor
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                        </CardContent>
                                    </Card>
                                </TabsContent>

                                <TabsContent value="global-transactions" className="space-y-4">
                                    {/* Render shared Transactions view filter and table */}
                                    <Card>
                                        <CardContent className="pt-4 pb-4">
                                            <div className="flex flex-wrap gap-3 items-end">
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-muted-foreground">Depi</label>
                                                    <Input type="date" value={filterDateDebut}
                                                        onChange={e => setFilterDateDebut(e.target.value)}
                                                        className="w-36 h-8 text-sm" />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-muted-foreground">Rive</label>
                                                    <Input type="date" value={filterDateFin}
                                                        onChange={e => setFilterDateFin(e.target.value)}
                                                        className="w-36 h-8 text-sm" />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-muted-foreground">Type</label>
                                                    <Select value={filterType} onValueChange={setFilterType}>
                                                        <SelectTrigger className="w-36 h-8 text-sm">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="all">Tout type</SelectItem>
                                                            <SelectItem value="depo_bs">Dépôt BS</SelectItem>
                                                            <SelectItem value="retrait_bs">Retrait BS</SelectItem>
                                                            <SelectItem value="depo_sol">Dépôt SOL</SelectItem>
                                                            <SelectItem value="transfer">Transfert</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-muted-foreground">Status</label>
                                                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                                                        <SelectTrigger className="w-36 h-8 text-sm">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="all">Tout status</SelectItem>
                                                            <SelectItem value="reussi">Konplète</SelectItem>
                                                            <SelectItem value="en_cours">Pending</SelectItem>
                                                            <SelectItem value="echoue">Echwe</SelectItem>
                                                            <SelectItem value="annule">Anile</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="flex flex-col gap-1 flex-1 min-w-40">
                                                    <label className="text-xs text-muted-foreground">Rechèch</label>
                                                    <div className="relative">
                                                        <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                                                        <Input
                                                            placeholder="Kliyan, kòd kont…"
                                                            value={filterSearch}
                                                            onChange={e => setFilterSearch(e.target.value)}
                                                            onKeyDown={e => e.key === 'Enter' && fetchTransactions(1)}
                                                            className="pl-8 h-8 text-sm"
                                                        />
                                                    </div>
                                                </div>
                                                <Button size="sm" variant="outline" className="h-8"
                                                    onClick={() => fetchTransactions(1)}>
                                                    <RefreshCw className="h-3 w-3 mr-1" /> Aplke
                                                </Button>
                                                <Button size="sm" variant="ghost" className="h-8 text-muted-foreground"
                                                    onClick={() => {
                                                        setFilterType('all'); setFilterStatus('all');
                                                        setFilterDateDebut(''); setFilterDateFin('');
                                                        setFilterSearch('');
                                                    }}>
                                                    Efase filtre
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Table */}
                                    <Card>
                                        <CardContent className="p-0">
                                            {txLoading ? (
                                                <div className="flex items-center justify-center py-12 text-muted-foreground">
                                                    <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Chajman…
                                                </div>
                                            ) : transactions.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                                                    <Filter className="h-8 w-8 opacity-30" />
                                                    <p className="text-sm">Pa gen tranzaksyon pou filtre sa</p>
                                                </div>
                                            ) : (
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>Dat</TableHead>
                                                            <TableHead>Kliyan</TableHead>
                                                            <TableHead>Kont</TableHead>
                                                            <TableHead>Type</TableHead>
                                                            <TableHead className="text-right">Montant</TableHead>
                                                            <TableHead>Status</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {transactions.map(tx => (
                                                            <TableRow key={tx.id}>
                                                                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                                                    {fmtDate((tx as any).date_transaction || tx.created_at)}
                                                                </TableCell>
                                                                <TableCell className="font-medium text-sm">
                                                                    {(tx as any).client_nom ?? '—'}
                                                                </TableCell>
                                                                <TableCell className="text-xs text-muted-foreground">
                                                                    {(tx as any).compte_code ?? '—'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <span className={cn(
                                                                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                                        TYPE_COLORS[tx.type] ?? 'bg-gray-100 text-gray-700'
                                                                    )}>
                                                                        {TYPE_LABELS[tx.type] ?? tx.type}
                                                                    </span>
                                                                </TableCell>
                                                                <TableCell className="text-right font-semibold text-sm">
                                                                    {fmtMoney(tx.montant)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <span className={cn(
                                                                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                                        STATUS_COLORS[tx.status] ?? 'bg-gray-100 text-gray-700'
                                                                    )}>
                                                                        {STATUS_LABELS[tx.status] ?? tx.status}
                                                                    </span>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                        </CardContent>

                                        {/* Pagination */}
                                        {totalTxPages > 1 && (
                                            <div className="flex items-center justify-between px-4 py-3 border-t">
                                                <p className="text-xs text-muted-foreground">
                                                    Page {txPage} / {totalTxPages} — {txCount} tranzaksyon
                                                </p>
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="outline" disabled={txPage <= 1}
                                                        onClick={() => fetchTransactions(txPage - 1)}>
                                                        <ChevronLeft className="h-4 w-4" />
                                                    </Button>
                                                    <Button size="sm" variant="outline" disabled={txPage >= totalTxPages}
                                                        onClick={() => fetchTransactions(txPage + 1)}>
                                                        <ChevronRight className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                </TabsContent>
                            </Tabs>
                        </>
                    ) : (
                        <>
                            {/* Agent Info Banner */}
                            <Card>
                                <CardContent className="pt-4 pb-4">
                                    <div className="flex flex-wrap items-center gap-4">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-lg">
                                            {(selectedAgent.client?.prenom?.[0] ?? 'A')}{(selectedAgent.client?.nom?.[0] ?? '')}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold text-lg">
                                                {selectedAgent.client?.prenom} {selectedAgent.client?.nom ?? selectedAgent.name}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {selectedAgent.type} • #{selectedAgent.code}
                                            </p>
                                        </div>
                                        <Badge className={selectedAgent.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                                            {selectedAgent.actif ? 'Aktif' : 'Inaktif'}
                                        </Badge>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* KPI Cards */}
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                <Card>
                                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                        <CardTitle className="text-sm font-medium">Balans</CardTitle>
                                        <Wallet className="h-4 w-4 text-blue-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{fmtMoney(selectedAgent.solde ?? 0)}</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                        <CardTitle className="text-sm font-medium">Komisyon</CardTitle>
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{fmtMoney(selectedAgent.commissionUsd ?? 0, 'USD')}</div>
                                        <p className="text-xs text-muted-foreground">{fmtMoney(selectedAgent.commission ?? 0)}</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                        <CardTitle className="text-sm font-medium">Kliyan Asiye</CardTitle>
                                        <Users className="h-4 w-4 text-purple-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{clientsCount}</div>
                                        <p className="text-xs text-muted-foreground">kliyan anba ajan sa</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                        <CardTitle className="text-sm font-medium">Demand Pending</CardTitle>
                                        <Clock className="h-4 w-4 text-orange-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className={cn("text-2xl font-bold", pendingCount > 0 && "text-orange-600")}>
                                            {pendingCount}
                                        </div>
                                        <p className="text-xs text-muted-foreground">demand retrait an atant</p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Tabs: Transactions | Kliyan */}
                            <Tabs defaultValue="transactions">
                                <TabsList>
                                    <TabsTrigger value="transactions">Tranzaksyon ({txCount})</TabsTrigger>
                                    <TabsTrigger value="clients">Kliyan Asiye ({clientsCount})</TabsTrigger>
                                </TabsList>

                                {/* ── TRANSACTIONS TAB ── */}
                                <TabsContent value="transactions" className="space-y-4">

                                    {/* Filters */}
                                    <Card>
                                        <CardContent className="pt-4 pb-4">
                                            <div className="flex flex-wrap gap-3 items-end">
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-muted-foreground">Depi</label>
                                                    <Input type="date" value={filterDateDebut}
                                                        onChange={e => setFilterDateDebut(e.target.value)}
                                                        className="w-36 h-8 text-sm" />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-muted-foreground">Rive</label>
                                                    <Input type="date" value={filterDateFin}
                                                        onChange={e => setFilterDateFin(e.target.value)}
                                                        className="w-36 h-8 text-sm" />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-muted-foreground">Type</label>
                                                    <Select value={filterType} onValueChange={setFilterType}>
                                                        <SelectTrigger className="w-36 h-8 text-sm">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="all">Tout type</SelectItem>
                                                            <SelectItem value="depo_bs">Dépôt BS</SelectItem>
                                                            <SelectItem value="retrait_bs">Retrait BS</SelectItem>
                                                            <SelectItem value="depo_sol">Dépôt SOL</SelectItem>
                                                            <SelectItem value="transfer">Transfert</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-muted-foreground">Status</label>
                                                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                                                        <SelectTrigger className="w-36 h-8 text-sm">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="all">Tout status</SelectItem>
                                                            <SelectItem value="reussi">Konplète</SelectItem>
                                                            <SelectItem value="en_cours">Pending</SelectItem>
                                                            <SelectItem value="echoue">Echwe</SelectItem>
                                                            <SelectItem value="annule">Anile</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="flex flex-col gap-1 flex-1 min-w-40">
                                                    <label className="text-xs text-muted-foreground">Rechèch</label>
                                                    <div className="relative">
                                                        <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                                                        <Input
                                                            placeholder="Kliyan, kòd kont…"
                                                            value={filterSearch}
                                                            onChange={e => setFilterSearch(e.target.value)}
                                                            onKeyDown={e => e.key === 'Enter' && fetchTransactions(1)}
                                                            className="pl-8 h-8 text-sm"
                                                        />
                                                    </div>
                                                </div>
                                                <Button size="sm" variant="outline" className="h-8"
                                                    onClick={() => fetchTransactions(1)}>
                                                    <RefreshCw className="h-3 w-3 mr-1" /> Aplke
                                                </Button>
                                                <Button size="sm" variant="ghost" className="h-8 text-muted-foreground"
                                                    onClick={() => {
                                                        setFilterType('all'); setFilterStatus('all');
                                                        setFilterDateDebut(''); setFilterDateFin('');
                                                        setFilterSearch('');
                                                    }}>
                                                    Efase filtre
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Table */}
                                    <Card>
                                        <CardContent className="p-0">
                                            {txLoading ? (
                                                <div className="flex items-center justify-center py-12 text-muted-foreground">
                                                    <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Chajman…
                                                </div>
                                            ) : transactions.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                                                    <Filter className="h-8 w-8 opacity-30" />
                                                    <p className="text-sm">Pa gen tranzaksyon pou filtre sa</p>
                                                </div>
                                            ) : (
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>Dat</TableHead>
                                                            <TableHead>Kliyan</TableHead>
                                                            <TableHead>Kont</TableHead>
                                                            <TableHead>Type</TableHead>
                                                            <TableHead className="text-right">Montant</TableHead>
                                                            <TableHead>Status</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {transactions.map(tx => (
                                                            <TableRow key={tx.id}>
                                                                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                                                    {fmtDate((tx as any).date_transaction || tx.created_at)}
                                                                </TableCell>
                                                                <TableCell className="font-medium text-sm">
                                                                    {(tx as any).client_nom ?? '—'}
                                                                </TableCell>
                                                                <TableCell className="text-xs text-muted-foreground">
                                                                    {(tx as any).compte_code ?? '—'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <span className={cn(
                                                                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                                        TYPE_COLORS[tx.type] ?? 'bg-gray-100 text-gray-700'
                                                                    )}>
                                                                        {TYPE_LABELS[tx.type] ?? tx.type}
                                                                    </span>
                                                                </TableCell>
                                                                <TableCell className="text-right font-semibold text-sm">
                                                                    {fmtMoney(tx.montant)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <span className={cn(
                                                                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                                        STATUS_COLORS[tx.status] ?? 'bg-gray-100 text-gray-700'
                                                                    )}>
                                                                        {STATUS_LABELS[tx.status] ?? tx.status}
                                                                    </span>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                        </CardContent>

                                        {/* Pagination */}
                                        {totalTxPages > 1 && (
                                            <div className="flex items-center justify-between px-4 py-3 border-t">
                                                <p className="text-xs text-muted-foreground">
                                                    Page {txPage} / {totalTxPages} — {txCount} tranzaksyon
                                                </p>
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="outline" disabled={txPage <= 1}
                                                        onClick={() => fetchTransactions(txPage - 1)}>
                                                        <ChevronLeft className="h-4 w-4" />
                                                    </Button>
                                                    <Button size="sm" variant="outline" disabled={txPage >= totalTxPages}
                                                        onClick={() => fetchTransactions(txPage + 1)}>
                                                        <ChevronRight className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                </TabsContent>

                                {/* ── CLIENTS TAB ── */}
                                <TabsContent value="clients" className="space-y-4">
                                    <div className="flex gap-3">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                placeholder="Rechèch kliyan…"
                                                value={clientSearch}
                                                onChange={e => setClientSearch(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && fetchClients(1)}
                                                className="pl-8"
                                            />
                                        </div>
                                        <Button variant="outline" onClick={() => fetchClients(1)}>
                                            <Search className="h-4 w-4 mr-1" /> Chèche
                                        </Button>
                                    </div>

                                    <Card>
                                        <CardContent className="p-0">
                                            {clientsLoading ? (
                                                <div className="flex items-center justify-center py-12 text-muted-foreground">
                                                    <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Chajman…
                                                </div>
                                            ) : clients.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                                                    <Users className="h-8 w-8 opacity-30" />
                                                    <p className="text-sm">
                                                        {selectedAgentId
                                                            ? 'Pa gen kliyan chaje — klike "Chèche" pou chaje yo'
                                                            : 'Seleksyone yon ajan anba'}
                                                    </p>
                                                    {selectedAgentId && (
                                                        <Button size="sm" variant="outline" onClick={() => fetchClients(1)}>
                                                            Chaje kliyan yo
                                                        </Button>
                                                    )}
                                                </div>
                                            ) : (
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>Kòd Kont</TableHead>
                                                            <TableHead>Kliyan</TableHead>
                                                            <TableHead>Telefòn</TableHead>
                                                            <TableHead className="text-right">Solde BS</TableHead>
                                                            <TableHead>Statut</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {clients.map(c => (
                                                            <TableRow key={c.compte_id}>
                                                                <TableCell className="font-mono text-sm">{c.compte_code}</TableCell>
                                                                <TableCell className="font-medium">
                                                                    {c.client?.prenom} {c.client?.nom}
                                                                </TableCell>
                                                                <TableCell className="text-sm text-muted-foreground">
                                                                    {c.client?.telephone ?? '—'}
                                                                </TableCell>
                                                                <TableCell className="text-right font-semibold text-sm">
                                                                    {fmtMoney(c.solde_bs ?? 0, 'HTG')}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Badge className={
                                                                        c.statut === 'active' || c.statut === 'ACTIVE'
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : 'bg-gray-100 text-gray-600'
                                                                    }>
                                                                        {c.statut ?? 'N/A'}
                                                                    </Badge>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                        </CardContent>
                                    </Card>
                                </TabsContent>
                            </Tabs>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}
